import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Smartphone, Gauge, Search } from "lucide-react";

const ScreenSite = () => (
  <div className="animate-screen bg-[#0b0f1c] text-white">
    <div className="flex items-center justify-between px-[5%] py-[3%] text-[9px] sm:text-[10px]">
      <span className="font-display font-semibold tracking-[0.3em] text-[var(--gold-2)]">MAISON</span>
      <span className="hidden gap-4 text-white/70 sm:flex"><span>Services</span><span>Gallery</span><span>Book</span></span>
      <span className="rounded-full bg-[var(--gold)] px-2 py-0.5 text-[8px] font-semibold text-black">Book now</span>
    </div>
    <div className="relative mx-[5%] h-[150px] overflow-hidden rounded-md sm:h-[190px]">
      <img src="/images/salon.jpg" alt="Demo salon website hero preview" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
      <div className="absolute left-[6%] top-1/2 -translate-y-1/2">
        <p className="text-[8px] uppercase tracking-[0.3em] text-[var(--gold-2)]">Demo concept</p>
        <p className="font-display mt-1 text-lg font-semibold leading-tight sm:text-2xl">Beauty,<br />crafted slowly.</p>
        <span className="mt-2 inline-block rounded-full border border-white/40 px-2 py-0.5 text-[8px]">Explore treatments</span>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 px-[5%] py-[4%]">
      {["Hair", "Skin", "Spa"].map((t, i) => (
        <div key={t} className="rounded-md bg-white/5 p-2">
          <div className="mb-1.5 h-8 rounded bg-gradient-to-br from-royal/40 to-gold/30 sm:h-12" style={{ opacity: 1 - i * 0.15 }} />
          <p className="text-[9px] font-semibold">{t} Studio</p>
          <p className="text-[7px] text-white/50">Signature rituals</p>
        </div>
      ))}
    </div>
    <div className="mx-[5%] mb-[4%] grid grid-cols-2 gap-2">
      <img src="/images/studio.jpg" alt="" className="h-20 w-full rounded-md object-cover sm:h-24" />
      <div className="rounded-md bg-gold/10 p-2">
        <p className="text-[9px] font-semibold text-[var(--gold-2)]">Reserve your visit</p>
        <div className="mt-2 space-y-1">
          <div className="h-2 rounded bg-white/10" /><div className="h-2 rounded bg-white/10" /><div className="h-3 w-1/2 rounded bg-[var(--gold)]" />
        </div>
      </div>
    </div>
    <div className="h-24 bg-gradient-to-b from-transparent to-[#060912]" />
  </div>
);

const Chip = ({ icon: Icon, label, className }) => (
  <div className={`glass animate-floaty absolute flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium text-white shadow-2xl ${className}`}>
    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gold/15 text-[var(--gold)]"><Icon className="h-3.5 w-3.5" /></span>
    {label}
  </div>
);

export const Laptop = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div data-testid="hero-laptop-mockup" onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto w-full max-w-[640px]" style={{ perspective: 1400 }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="relative rounded-[18px] border border-white/15 bg-gradient-to-b from-[#1b2340] to-[#0c1226] p-[2.2%] shadow-[0_60px_120px_-40px_rgba(43,91,255,.55)]">
          <div className="absolute left-1/2 top-[1%] h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-white/20" />
          <div className="relative aspect-[16/10] overflow-hidden rounded-[8px] bg-black">
            <ScreenSite />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.04] to-white/[0.1]" />
          </div>
        </div>
        <div className="relative mx-[-6%] h-4 rounded-b-[18px] rounded-t-[4px] bg-gradient-to-b from-[#c7cfe4] via-[#7d88a6] to-[#3a4360] sm:h-5">
          <div className="absolute left-1/2 top-0 h-1.5 w-[16%] -translate-x-1/2 rounded-b-lg bg-[#2b3350]" />
        </div>
        <div className="mx-auto h-6 w-[80%] rounded-[50%] bg-royal/30 blur-2xl" />
        <div className="pointer-events-none absolute inset-0" style={{ transform: "translateZ(60px)" }}>
          <Chip icon={Smartphone} label="Mobile-friendly" className="-left-2 top-[12%] sm:-left-10" />
          <Chip icon={Gauge} label="Fast loading" className="-right-2 top-[38%] sm:-right-8 [animation-delay:1.2s]" />
          <Chip icon={Search} label="SEO-ready" className="bottom-[18%] left-[8%] [animation-delay:2.4s]" />
        </div>
      </motion.div>
    </div>
  );
};
